<template>
  <div class="verify-screen">
    <!-- 顶部装饰条 -->
    <div class="top-decoration"></div>

    <!-- 主体内容 -->
    <div class="main-content">
      <!-- 标题与操作区 -->
      <div class="header">
        <div class="title-wrapper">
          <div class="title-icon">
            <i class="fas fa-utensils"></i>
          </div>
          <div class="title-text">
            <h1 class="title-main">餐券核销</h1>
            <p class="title-sub">Buffet Verification</p>
          </div>
        </div>

        <div class="header-actions">
          <div class="selection-panel">
            <div class="selection-row">
              <div class="selection-label">当前餐厅</div>
              <div class="selection-content">
                <span class="selection-value">{{ currentShopName || '未选择餐厅' }}</span>
              </div>
            </div>

            <div class="selection-row">
              <div class="selection-label">卡券商品</div>
              <div class="selection-content">
                <a-select
                  v-model:value="selectedShopFoodIds"
                  mode="multiple"
                  class="coupon-select"
                  :options="shopFoodOptions"
                  :loading="isShopFoodLoading"
                  :disabled="!currentShopId"
                  :max-tag-count="3"
                  placeholder="请选择可核销的卡券商品"
                  option-filter-prop="label"
                  show-search
                />
              </div>
            </div>
          </div>

          <div class="mode-switch">
            <button
              class="mode-btn"
              :class="{ active: verificationMode === 'input' }"
              @click="handleModeChange('input')"
            >
              输入核销
            </button>
            <button
              class="mode-btn"
              :class="{ active: verificationMode === 'scan' }"
              @click="handleModeChange('scan')"
            >
              扫码核销
            </button>
          </div>

          <div v-if="verificationMode === 'input'" class="input-wrapper">
            <div class="input-with-suggestions">
              <div class="input-shell">
                <a-input
                  ref="inputRef"
                  v-model:value="inputCouponCode"
                  class="order-input"
                  placeholder="输入卡券码或二维码内容后回车核销"
                  @keyup.enter="handleVerifyInput"
                />
                <button
                  v-if="inputCouponCode"
                  class="clear-btn"
                  type="button"
                  @mousedown.prevent
                  @click="handleClearInput"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
            <button class="verify-btn" @click="handleVerifyInput">
              <i class="fas fa-check-circle mr-2"></i>核销
            </button>
          </div>
        </div>
      </div>

      <div v-if="verificationMode === 'scan'" class="scan-wrapper">
        <div class="scan-toolbar">
          <p class="scan-tip">
            摄像头已持续扫描，可连续扫码核销（待处理：{{ scanQueue.length }}）
          </p>
          <p class="scan-status">{{ isScannerRunning ? '扫描中' : '摄像头未启动' }}</p>
        </div>
        <div class="scanner-panel" :class="{ idle: !isScannerRunning }">
          <div :id="scannerContainerId" class="scanner-view"></div>
          <div v-if="!isScannerRunning" class="scanner-empty">
            <span>无法启动摄像头，请检查 HTTPS、摄像头权限或设备可用性</span>
          </div>
        </div>
        <div class="scan-result-card">
          <p class="scan-result-title">最近识别内容</p>
          <p v-if="lastScannedAt" class="scan-result-time">
            {{ lastScannedAt }}
          </p>
          <p class="scan-result-text">
            {{ lastScannedRawText || '暂无识别结果' }}
          </p>
          <div v-if="lastVerifiedFoodLines.length" class="scan-order-highlight">
            <p class="scan-order-highlight-title">本次核销结果</p>
            <p
              v-for="(item, index) in lastVerifiedFoodLines"
              :key="`${item}-${index}`"
              class="scan-order-highlight-item"
            >
              {{ item }}
            </p>
          </div>
        </div>
      </div>

      <!-- 核销结果提示 -->
      <div v-if="successMessage" class="success-toast">
        <i class="fas fa-check-circle mr-2"></i>{{ successMessage }}
      </div>
    </div>

    <!-- 底部装饰条 -->
    <div class="bottom-decoration"></div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, onBeforeUnmount, onMounted } from 'vue';
  import { Html5Qrcode } from 'html5-qrcode';
  import type { CouponUseResultModel } from '@/service/order';
  import { useCustomerCoupon } from '@/service/order';
  import { fetchShopList } from '@/service/base';
  import { readSelectedShop } from '@/utils/selectedShop';
  import { fetchCategoryItemList } from '@/service/food';
  import type { ShopDetailModel } from '@/model/shop';
  import { message } from 'ant-design-vue';
  import okSoundSrc from '@/assets/audio/ok.mp3';
  import errorSoundSrc from '@/assets/audio/error.mp3';

  type VerificationMode = 'input' | 'scan';
  interface ShopFoodOption {
    shopFoodId: number;
    shopFoodName: string;
  }

  // 输入框卡券码/二维码
  const inputCouponCode = ref('');
  const inputRef = ref<{ focus?: () => void } | null>(null);

  // 核销结果提示
  const successMessage = ref('');
  const isVerifying = ref(false);
  const verificationMode = ref<VerificationMode>('input');
  const scanQueue = ref<string[]>([]);
  const isQueueProcessing = ref(false);
  const lastScannedRawText = ref('');
  const lastScannedAt = ref('');
  const lastVerifiedFoodLines = ref<string[]>([]);

  // 店铺选择相关
  const shopList = ref<ShopDetailModel[]>([]);
  // 当前生效的店铺ID（仅来自 URL 或下拉选择）
  const currentShopId = ref<number | null>(null);
  const shopFoodOptions = ref<{ label: string; value: number }[]>([]);
  const selectedShopFoodIds = ref<number[]>([]);
  const isShopFoodLoading = ref(false);

  const currentShopName = computed(() => {
    if (!currentShopId.value) return '';
    return shopList.value.find((shop) => shop.shopId === currentShopId.value)?.shopName || '';
  });

  // H5 扫码
  const scannerContainerId = 'buffet-qrcode-reader';
  const isScannerRunning = ref(false);
  let html5Qrcode: Html5Qrcode | null = null;
  const lastScanTimeMap = new Map<string, number>();
  let okAudio: HTMLAudioElement | null = null;
  let errorAudio: HTMLAudioElement | null = null;

  // 加载当前账号可见的餐厅，用于解析缓存餐厅名称。
  const loadShopList = async () => {
    try {
      const response = await fetchShopList({
        pageNum: 1,
        pageSize: 100,
        data: { shopStatus: 0 },
      });
      if (response.code === 200 && response.result && response.result.list) {
        shopList.value = response.result.list;
      }
    } catch (error) {
      console.error('加载店铺列表失败:', error);
    }
  };

  const loadShopFoodOptions = async (shopId: number) => {
    isShopFoodLoading.value = true;
    try {
      const response = await fetchCategoryItemList({
        pageNum: 1,
        pageSize: 20,
        data: {
          foodPropType: 2,
          shopId,
        },
      });

      if (response.code === 200) {
        const list = (response.result?.list || []) as ShopFoodOption[];
        shopFoodOptions.value = list
          .filter((item) => item.shopFoodId && item.shopFoodName)
          .map((item) => ({
            label: item.shopFoodName,
            value: item.shopFoodId,
          }));
        selectedShopFoodIds.value = selectedShopFoodIds.value.filter((id) =>
          shopFoodOptions.value.some((option) => option.value === id),
        );
        return;
      }

      shopFoodOptions.value = [];
      selectedShopFoodIds.value = [];
      message.error(response.msg || '加载卡券商品失败');
    } catch (error) {
      console.error('加载卡券商品失败:', error);
      shopFoodOptions.value = [];
      selectedShopFoodIds.value = [];
      message.error('加载卡券商品失败，请稍后重试');
    } finally {
      isShopFoodLoading.value = false;
    }
  };

  // 初始化
  onMounted(async () => {
    initAudioPlayers();
    await loadShopList();
    const cachedShopId = readSelectedShop();
    if (cachedShopId) {
      currentShopId.value = cachedShopId;
      await loadShopFoodOptions(cachedShopId);
    }
  });

  onBeforeUnmount(async () => {
    await stopScanner();
    okAudio?.pause();
    errorAudio?.pause();
  });

  const normalizeCouponCode = (rawText: string): string => rawText.trim();

  const showResultToast = (text: string) => {
    successMessage.value = text;
    setTimeout(() => {
      successMessage.value = '';
    }, 2000);
  };

  const initAudioPlayers = () => {
    if (!okAudio) {
      okAudio = new Audio(okSoundSrc);
      okAudio.preload = 'auto';
      okAudio.playsInline = true;
      okAudio.volume = 1;
      okAudio.load();
    }
    if (!errorAudio) {
      errorAudio = new Audio(errorSoundSrc);
      errorAudio.preload = 'auto';
      errorAudio.playsInline = true;
      errorAudio.volume = 1;
      errorAudio.load();
    }
  };

  const playSound = (audio: HTMLAudioElement | null, src: string) => {
    const target = audio ?? new Audio(src);
    target.playsInline = true;
    target.volume = 1;
    try {
      target.currentTime = 0;
      const playPromise = target.play();
      if (playPromise) {
        void playPromise.catch(() => {
          const clone = target.cloneNode(true) as HTMLAudioElement;
          clone.playsInline = true;
          clone.volume = 1;
          void clone.play().catch(() => {
            // ignore play errors
          });
        });
      }
    } catch (error) {
      // ignore play errors
    }
  };

  const unlockAudio = () => {
    initAudioPlayers();
    try {
      [okAudio, errorAudio].forEach((audio) => {
        if (!audio) return;
        audio.muted = true;
        void audio.play().then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
        });
      });
    } catch (error) {
      // ignore unlock errors
    }
  };

  const playSuccessSound = () => {
    playSound(okAudio, okSoundSrc);
  };

  const playErrorSound = () => {
    playSound(errorAudio, errorSoundSrc);
  };

  const updateLastVerifiedFoods = (result: Partial<CouponUseResultModel>) => {
    const couponName = String(result?.couponName || '').trim();
    const couponNumber = String(result?.couponNumber || '').trim();
    const couponCount = Number(result?.couponCount);
    const lines: string[] = [];

    if (couponName) {
      lines.push(
        Number.isFinite(couponCount) && couponCount > 0
          ? `${couponName} x ${couponCount}`
          : couponName,
      );
    }
    if (couponNumber) {
      lines.push(`券码：${couponNumber}`);
    }

    lastVerifiedFoodLines.value = lines;
  };

  const buildCouponUsePayload = (code: string) => {
    const normalizedCode = normalizeCouponCode(code);
    return {
      couponNumbers: normalizedCode.startsWith('1|') ? [] : [normalizedCode],
      qrcode: normalizedCode.startsWith('1|') ? normalizedCode : '',
      shopFoodIds: selectedShopFoodIds.value,
      shopId: currentShopId.value,
    };
  };

  const verifyCoupon = async (code: string) => {
    if (isVerifying.value) {
      return;
    }
    isVerifying.value = true;

    const shopId = currentShopId.value;
    if (!shopId) {
      message.warning('请从右下角快速菜单选择餐厅');
      playErrorSound();
      isVerifying.value = false;
      return;
    }

    if (!selectedShopFoodIds.value.length) {
      message.warning('请先选择卡券商品');
      playErrorSound();
      isVerifying.value = false;
      return;
    }

    try {
      const normalizedCode = normalizeCouponCode(code);
      if (!normalizedCode) {
        message.warning('请输入卡券码或二维码内容');
        playErrorSound();
        return;
      }

      const resp = await useCustomerCoupon(buildCouponUsePayload(normalizedCode));
      if (resp.code === 200) {
        const couponName = resp.result?.couponName || '卡券';
        const couponCount = Number(resp.result?.couponCount);
        const successText =
          Number.isFinite(couponCount) && couponCount > 0
            ? `${couponName} 核销成功 x ${couponCount}`
            : `${couponName} 核销成功`;
        showResultToast(successText);
        message.success(successText);
        updateLastVerifiedFoods(resp.result || {});
        playSuccessSound();
      } else {
        showResultToast(`核销失败：${resp.msg || '接口返回错误'}`);
        message.error(resp.msg || '核销失败');
        playErrorSound();
      }
    } catch (e) {
      console.error('核销异常:', e);
      showResultToast(`核销异常：${e instanceof Error ? e.message : '未知错误'}`);
      message.error('核销异常，请稍后重试');
      playErrorSound();
    } finally {
      isVerifying.value = false;
    }
  };

  const processScanQueue = async () => {
    if (isQueueProcessing.value) {
      return;
    }
    isQueueProcessing.value = true;
    try {
      while (scanQueue.value.length > 0) {
        const code = scanQueue.value.shift();
        if (!code) continue;
        inputCouponCode.value = code;
        await verifyCoupon(code);
      }
    } finally {
      inputCouponCode.value = '';
      isQueueProcessing.value = false;
    }
  };

  const pushScanToQueue = (code: string) => {
    const now = Date.now();
    const previous = lastScanTimeMap.get(code) || 0;
    // 同一个二维码短时间内会重复触发识别，这里做3秒节流去重
    if (now - previous < 3000) {
      return;
    }
    lastScanTimeMap.set(code, now);
    scanQueue.value.push(code);
    void processScanQueue();
  };

  const handleScanSuccess = (decodedText: string) => {
    const code = normalizeCouponCode(decodedText);
    if (!code) return;
    lastScannedRawText.value = decodedText;
    lastScannedAt.value = new Date().toLocaleString('zh-CN', { hour12: false });
    pushScanToQueue(code);
  };

  const isHttpsOrLocal = (): boolean => {
    const { protocol, hostname } = window.location;
    return protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1';
  };

  const startScanner = async (): Promise<boolean> => {
    if (isScannerRunning.value) return true;
    if (!isHttpsOrLocal()) {
      message.warning('扫码需要 HTTPS 环境（localhost 除外）');
      return false;
    }

    try {
      const cameras = await Html5Qrcode.getCameras();
      if (!cameras.length) {
        message.warning('未检测到可用摄像头');
        return false;
      }

      if (!html5Qrcode) {
        html5Qrcode = new Html5Qrcode(scannerContainerId);
      }

      const isMobile = window.innerWidth <= 768;
      const qrboxSize = isMobile ? 180 : 220;
      const startConfig = {
        fps: 12,
        qrbox: { width: qrboxSize, height: qrboxSize },
      };

      const startErrorCallback = () => {
        // 忽略单次识别失败，避免频繁提示影响扫码体验
      };

      // 1) 首选后置摄像头约束，移动端优先启用 environment
      let started = false;
      try {
        await html5Qrcode.start(
          { facingMode: { exact: 'environment' } },
          startConfig,
          handleScanSuccess,
          startErrorCallback,
        );
        started = true;
      } catch {
        // 2) exact 失败时再降级为非 exact 的 environment
        try {
          await html5Qrcode.start(
            { facingMode: 'environment' },
            startConfig,
            handleScanSuccess,
            startErrorCallback,
          );
          started = true;
        } catch {
          // 3) 仍失败则按设备列表匹配后置摄像头
          const backCamera =
            cameras.find((item) => /back|rear|environment|后置|后摄/gi.test(item.label)) ||
            cameras[cameras.length - 1] ||
            cameras[0];
          await html5Qrcode.start(
            backCamera.id,
            startConfig,
            handleScanSuccess,
            startErrorCallback,
          );
          started = true;
        }
      }

      if (!started) {
        throw new Error('无法启动后置摄像头');
      }

      isScannerRunning.value = true;
      return true;
    } catch (error) {
      console.error('启动扫码失败:', error);
      message.error('启动扫码失败，请检查摄像头权限');
      await stopScanner();
      return false;
    }
  };

  const stopScanner = async () => {
    scanQueue.value = [];
    lastScanTimeMap.clear();
    if (!html5Qrcode) {
      isScannerRunning.value = false;
      return;
    }
    try {
      if (html5Qrcode.isScanning) {
        await html5Qrcode.stop();
      }
      await html5Qrcode.clear();
    } catch (error) {
      console.error('关闭扫码失败:', error);
    } finally {
      isScannerRunning.value = false;
      html5Qrcode = null;
    }
  };

  const handleModeChange = async (mode: VerificationMode) => {
    unlockAudio();
    if (verificationMode.value === mode) {
      return;
    }
    verificationMode.value = mode;
    inputCouponCode.value = '';

    if (mode === 'scan') {
      await nextTick();
      const started = await startScanner();
      if (!started) {
        verificationMode.value = 'input';
        await nextTick();
        inputRef.value?.focus?.();
      }
      return;
    }

    await stopScanner();
    await nextTick();
    inputRef.value?.focus?.();
  };

  // 通过输入框触发核销
  const handleVerifyInput = async () => {
    unlockAudio();
    const couponCode = inputCouponCode.value.trim();
    if (!couponCode) {
      message.warning('请输入卡券码或二维码内容');
      return;
    }
    await verifyCoupon(couponCode);
    inputCouponCode.value = '';
  };

  const handleClearInput = () => {
    inputCouponCode.value = '';
    inputRef.value?.focus?.();
  };
</script>

<style scoped>
  .verify-screen {
    width: 100vw;
    height: 100vh;
    background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: 'PingFang SC', 'Microsoft YaHei', 'Arial', sans-serif;
    position: relative;
    color: #ffffff;
  }

  .top-decoration,
  .bottom-decoration {
    height: 4px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(59, 130, 246, 0.4) 25%,
      rgba(37, 99, 235, 0.6) 50%,
      rgba(59, 130, 246, 0.4) 75%,
      transparent 100%
    );
    position: relative;
    z-index: 10;
    animation: shimmer 3s infinite;
  }

  .main-content {
    flex: 1;
    padding: 40px 60px;
    display: flex;
    flex-direction: column;
    position: relative;
    background: linear-gradient(135deg, #1a1d29 0%, #252936 50%, #1a1d29 100%);
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 18px;
  }

  .header-actions {
    width: min(680px, 56%);
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 12px;
  }

  .selection-panel {
    width: 100%;
    padding: 14px 16px;
    border-radius: 18px;
    border: 1px solid rgba(59, 130, 246, 0.28);
    background: rgba(8, 15, 30, 0.72);
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .selection-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .selection-label {
    width: 72px;
    padding-top: 10px;
    color: rgba(255, 255, 255, 0.72);
    font-size: 14px;
    flex-shrink: 0;
  }

  .selection-content {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .selection-value {
    min-height: 40px;
    padding: 9px 14px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.06);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
  }

  .change-shop-btn {
    height: 38px;
    padding: 0 14px;
    border-radius: 999px;
    border: 1px solid rgba(96, 165, 250, 0.45);
    background: rgba(37, 99, 235, 0.18);
    color: #bfdbfe;
    cursor: pointer;
    transition: all 0.25s ease;
  }

  .change-shop-btn:hover {
    background: rgba(37, 99, 235, 0.3);
    border-color: rgba(96, 165, 250, 0.65);
  }

  .coupon-select {
    width: 100%;
  }

  :deep(.coupon-select .ant-select-selector) {
    min-height: 42px !important;
    border-radius: 12px !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    background: rgba(255, 255, 255, 0.06) !important;
    box-shadow: none !important;
    padding: 4px 10px !important;
  }

  :deep(.coupon-select .ant-select-selection-placeholder) {
    color: rgba(255, 255, 255, 0.38) !important;
  }

  :deep(.coupon-select .ant-select-selection-item) {
    color: #ffffff !important;
  }

  :deep(.coupon-select .ant-select-selection-search-input) {
    color: #ffffff !important;
  }

  .title-wrapper {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .title-icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.15));
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: #60a5fa;
    box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
    border: 1px solid rgba(59, 130, 246, 0.3);
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
  }

  .title-icon:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(37, 99, 235, 0.25));
    border-color: rgba(59, 130, 246, 0.5);
    box-shadow: 0 6px 24px rgba(59, 130, 246, 0.4);
    transform: scale(1.05);
  }

  .title-icon:active {
    transform: scale(0.95);
  }

  .title-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .title-main {
    font-size: 40px;
    font-weight: 700;
    margin: 0;
    letter-spacing: 2px;
  }

  .title-sub {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.7);
    letter-spacing: 3px;
    text-transform: uppercase;
    margin: 0;
  }

  .mode-switch {
    width: fit-content;
    margin-bottom: 0;
    padding: 4px;
    border-radius: 999px;
    background: rgba(11, 20, 35, 0.8);
    border: 1px solid rgba(59, 130, 246, 0.35);
    display: inline-flex;
    gap: 4px;
  }

  .mode-btn {
    border: none;
    border-radius: 999px;
    padding: 8px 18px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.75);
    background: transparent;
    cursor: pointer;
    transition: all 0.25s ease;
  }

  .mode-btn.active {
    color: #ffffff;
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.45);
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    width: 100%;
    margin-bottom: 0;
  }

  .input-with-suggestions {
    flex: 1;
  }

  .input-shell {
    position: relative;
    width: 100%;
  }

  .order-input {
    width: 100%;
  }

  /* 自定义 Input 样式 */
  :deep(.order-input .ant-input) {
    border-radius: 999px !important;
    border: 1px solid rgba(255, 255, 255, 0.25) !important;
    background: rgba(0, 0, 0, 0.3) !important;
    padding: 10px 14px !important;
    height: 44px !important;
    color: #ffffff !important;
    font-size: 18px !important;
  }

  :deep(.order-input .ant-input::placeholder) {
    color: rgba(255, 255, 255, 0.4) !important;
  }

  :deep(.order-input.ant-input-affix-wrapper-focused .ant-input) {
    border-color: rgba(59, 130, 246, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3) !important;
  }

  /* 自定义清除按钮，位置在输入框右侧中间 */
  .clear-btn {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 10px;
    padding: 0;
    z-index: 10;
  }

  .clear-btn:hover {
    background: rgba(255, 255, 255, 0.4);
  }

  .success-toast {
    margin-bottom: 16px;
    align-self: flex-start;
    padding: 8px 16px;
    border-radius: 999px;
    background: rgba(34, 197, 94, 0.15);
    border: 1px solid rgba(34, 197, 94, 0.6);
    color: #bbf7d0;
    display: inline-flex;
    align-items: center;
    font-size: 16px;
  }

  .scan-wrapper {
    margin-bottom: 16px;
    width: min(1260px, 100%);
    margin-left: auto;
    display: grid;
    grid-template-columns: 620px minmax(0, 1fr);
    gap: 10px 12px;
    align-items: start;
  }

  .scan-result-card {
    margin-top: 0;
    grid-column: 2;
    grid-row: 2;
    min-width: 0;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid rgba(59, 130, 246, 0.35);
    background: rgba(13, 16, 26, 0.55);
  }

  .scan-result-title {
    margin: 0 0 6px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.75);
  }

  .scan-result-text {
    margin: 0;
    color: #e2e8f0;
    font-size: 13px;
    line-height: 1.4;
    word-break: break-all;
  }

  .scan-result-time {
    margin: 0 0 6px;
    color: #94a3b8;
    font-size: 12px;
  }

  .scan-order-highlight {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed rgba(248, 113, 113, 0.45);
  }

  .scan-order-highlight-title {
    margin: 0 0 4px;
    color: #fca5a5;
    font-size: 14px;
    font-weight: 700;
  }

  .scan-order-highlight-item {
    margin: 0;
    color: #ef4444;
    font-size: 20px;
    line-height: 1.5;
    font-weight: 700;
    word-break: break-all;
  }

  .scan-toolbar {
    grid-column: 1 / -1;
    grid-row: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 0;
    flex-wrap: wrap;
  }

  .scan-tip {
    margin: 0;
    color: rgba(255, 255, 255, 0.82);
    font-size: 14px;
  }

  .scan-status {
    margin: 0;
    color: #6ee7b7;
    font-size: 13px;
  }

  .scanner-panel {
    grid-column: 1;
    grid-row: 2;
    position: relative;
    border-radius: 16px;
    border: 1px solid rgba(59, 130, 246, 0.35);
    padding: 10px;
    background: rgba(13, 16, 26, 0.72);
    max-width: 620px;
    min-height: 440px;
  }

  .scanner-view {
    width: 100%;
    height: 420px;
    min-height: 420px;
    overflow: hidden;
    border-radius: 10px;
  }

  .scanner-empty {
    position: absolute;
    inset: 10px;
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    padding: 16px;
  }

  :deep(.scanner-view video) {
    border-radius: 10px;
  }

  :deep(.scanner-view > div) {
    border: none !important;
    height: 100% !important;
  }

  :deep(.scanner-view video) {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
  }

  .verify-btn {
    padding: 10px 24px;
    border-radius: 999px;
    border: none;
    background: linear-gradient(135deg, #f97316, #ea580c);
    color: #ffffff;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 15px rgba(249, 115, 22, 0.4);
    transition: all 0.3s ease;
    white-space: nowrap;
  }

  .verify-btn:hover {
    background: linear-gradient(135deg, #ea580c, #dc2626);
    box-shadow: 0 6px 20px rgba(249, 115, 22, 0.5);
    transform: translateY(-1px);
  }

  /* 动画与滚动条 */
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  /* 响应式 */
  @media (max-width: 1366px) {
    .main-content {
      padding: 30px 40px;
    }

    .title-main {
      font-size: 32px;
    }

    .title-sub {
      font-size: 16px;
    }

    .header-actions,
    .scan-wrapper {
      width: min(1160px, 88%);
    }

    .verify-btn {
      font-size: 16px;
      padding: 8px 20px;
    }
  }

  @media (max-width: 768px) {
    .verify-screen {
      height: auto;
      min-height: 100vh;
      overflow: auto;
    }

    .main-content {
      padding: 18px 14px 24px;
    }

    .header {
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
      margin-bottom: 10px;
    }

    .header-actions {
      width: 100%;
      align-items: stretch;
      gap: 10px;
    }

    .title-wrapper {
      gap: 12px;
    }

    .title-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      font-size: 22px;
    }

    .title-main {
      font-size: 26px;
      letter-spacing: 1px;
    }

    .title-sub {
      font-size: 13px;
      letter-spacing: 1px;
    }

    .mode-switch {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr 1fr;
      margin-bottom: 0;
    }

    .mode-btn {
      width: 100%;
      text-align: center;
    }

    .input-wrapper {
      min-width: 0;
      width: 100%;
      flex-direction: column;
      align-items: stretch;
    }

    .selection-row {
      flex-direction: column;
      gap: 8px;
    }

    .selection-label {
      width: auto;
      padding-top: 0;
    }

    .selection-content {
      width: 100%;
    }

    :deep(.order-input .ant-input) {
      font-size: 16px !important;
      height: 42px !important;
    }

    .verify-btn {
      width: auto;
      align-self: center;
      font-size: 16px;
      padding: 8px 22px;
    }

    .scan-wrapper {
      width: 100%;
      margin-bottom: 12px;
      margin-left: 0;
      display: block;
    }

    .scan-toolbar {
      margin-bottom: 8px;
    }

    .scan-result-card {
      margin-top: 8px;
      grid-column: auto;
      grid-row: auto;
    }

    .scanner-panel {
      width: 100%;
      max-width: none;
      padding: 8px;
      min-height: 380px;
    }

    .scanner-view {
      height: 360px;
      min-height: 360px;
    }

    .scanner-empty {
      min-height: 360px;
    }

    .success-toast {
      width: 100%;
      box-sizing: border-box;
      justify-content: center;
    }

    .shop-select-modal {
      min-width: auto;
      width: calc(100vw - 28px);
      padding: 20px;
    }
  }

  /* 店铺选择模态框样式 */
  .shop-select-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
    backdrop-filter: blur(4px);
  }

  .shop-select-modal {
    background: linear-gradient(135deg, #252936 0%, #1a1d29 100%);
    border-radius: 16px;
    padding: 24px 28px;
    min-width: 360px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(59, 130, 246, 0.2);
    text-align: center;
    border: 1px solid rgba(59, 130, 246, 0.2);
  }

  .shop-select-title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #ffffff;
  }

  .shop-select-body {
    margin-bottom: 20px;
  }

  .shop-select-dropdown {
    width: 100%;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid rgba(59, 130, 246, 0.3);
    background: rgba(37, 42, 58, 0.9);
    color: #ffffff;
    font-size: 16px;
    outline: none;
    transition: all 0.3s ease;
  }

  .shop-select-dropdown:hover {
    border-color: rgba(59, 130, 246, 0.5);
  }

  .shop-select-dropdown:focus {
    border-color: rgba(59, 130, 246, 0.8);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }

  .shop-select-dropdown option {
    background: #252936;
    color: #ffffff;
  }

  .shop-select-footer {
    display: flex;
    justify-content: center;
  }

  .shop-select-btn.confirm {
    padding: 8px 32px;
    border-radius: 999px;
    border: none;
    background: linear-gradient(135deg, #f97316, #ea580c);
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(249, 115, 22, 0.4);
    transition: all 0.3s ease;
  }

  .shop-select-btn.confirm:hover {
    background: linear-gradient(135deg, #ea580c, #dc2626);
    box-shadow: 0 6px 20px rgba(249, 115, 22, 0.5);
    transform: translateY(-1px);
  }
</style>
